import React, { useEffect, useMemo, useRef, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, ExpressCheckoutElement, useStripe, useElements } from "@stripe/react-stripe-js";
import axios from "axios";

const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "";
const stripePromise = STRIPE_PK ? loadStripe(STRIPE_PK) : null;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Inner widget — rendered inside <Elements> so it has access to Stripe.
 *
 * Flow on wallet tap:
 *   1. POST /api/express-checkout/create-intent → get client_secret + intent_id
 *   2. Call stripe.confirmPayment with the new clientSecret + return_url
 *      pointing at /checkout/success?session_id=<intent_id>
 *
 * On success, the existing CheckoutSuccessPage polls the status endpoint and
 * shows the same confirmation as the regular Stripe Checkout flow.
 */
const ExpressInner = ({ getPayload, total, currency = "eur", onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  // Last shipping quote for the wallet-selected country (set during the
  // shippingaddresschange events, used as sanity context on confirm).
  const lastQuoteRef = useRef(null);

  if (!stripe || !elements) return null;

  // Open the wallet sheet requiring a shipping address: without this the
  // buyer's real country is unknown and everyone was charged the Italian rate.
  const handleClick = (event) => {
    const payload = getPayload();
    const shipCents = Math.round((payload.shipping_cost || 0) * 100);
    event.resolve({
      shippingAddressRequired: true,
      shippingRates: [
        { id: "standard", displayName: "Spedizione standard", amount: shipCents },
      ],
    });
  };

  // Buyer picked/changed address in the wallet sheet → recompute shipping for
  // the REAL country server-side and update the displayed total.
  const handleShippingAddressChange = async (event) => {
    try {
      const payload = getPayload();
      const { data } = await axios.post(`${API}/express-checkout/shipping-quote`, {
        items: payload.items,
        country: event.address?.country || "",
        postal_code: event.address?.postal_code || "",
        city: event.address?.city || "",
        coupon_code: payload.coupon_code || null,
      });
      lastQuoteRef.current = data;
      elements.update({ amount: Math.round(data.total * 100) });
      event.resolve({
        shippingRates: [
          {
            id: "standard",
            displayName: `Spedizione ${data.country_name || ""}`.trim(),
            amount: Math.round(data.shipping_cost * 100),
          },
        ],
      });
    } catch (e) {
      // Country not covered by any shipping rule → reject the address
      lastQuoteRef.current = null;
      event.reject();
    }
  };

  const handleConfirm = async (event) => {
    if (busy) return;
    setBusy(true);
    try {
      // 1. Ask backend to create a fresh PaymentIntent matching the cart total.
      //    Pass the wallet's REAL shipping address: the backend recomputes the
      //    shipping zone + cost from the ISO country (never trusts defaults).
      const payload = getPayload();
      const sa = event.shippingAddress || {};
      const saAddr = sa.address || {};
      payload.express_address = {
        name: sa.name || event.billingDetails?.name || "",
        email: event.billingDetails?.email || "",
        phone: event.billingDetails?.phone || "",
        country: saAddr.country || "",
        city: saAddr.city || "",
        postal_code: saAddr.postal_code || "",
        line1: saAddr.line1 || "",
        line2: saAddr.line2 || "",
      };
      const { data } = await axios.post(`${API}/express-checkout/create-intent`, payload);
      const clientSecret = data?.client_secret;
      const intentId = data?.intent_id;
      if (!clientSecret || !intentId) throw new Error("Risposta non valida dal server");

      // 2. Confirm payment via Stripe.js using the wallet selected by the user.
      const returnUrl = `${window.location.origin}/checkout/success?session_id=${encodeURIComponent(intentId)}`;
      const { error } = await stripe.confirmPayment({
        elements,
        clientSecret,
        confirmParams: { return_url: returnUrl },
      });
      // If confirmPayment redirects, this code is unreachable. If it returns
      // here it means there was a validation error before charging.
      if (error) {
        const msg = error.message || "Pagamento non completato";
        onError && onError(msg);
        setBusy(false);
        event?.paymentFailed && event.paymentFailed({ reason: "fail" });
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Errore Express Checkout";
      onError && onError(msg);
      setBusy(false);
      event?.paymentFailed && event.paymentFailed({ reason: "fail" });
    }
  };

  return (
    <div className="mb-3" data-testid="express-wallet-container">
      <ExpressCheckoutElement
        onClick={handleClick}
        onShippingAddressChange={handleShippingAddressChange}
        onConfirm={handleConfirm}
        options={{
          paymentMethods: { applePay: "always", googlePay: "always", link: "auto", paypal: "never" },
          buttonHeight: 48,
          emailRequired: true,
          phoneNumberRequired: true,
        }}
      />
    </div>
  );
};

/**
 * Public wrapper — mounts Stripe Elements with PaymentElement intent mode so
 * the ExpressCheckoutElement can render without an upfront client_secret
 * (we create the PaymentIntent on click — saves an API call when the user
 * never taps the wallet button).
 */
export const ExpressWalletButtons = ({ amount, currency = "eur", getPayload, onError, disabled }) => {
  const options = useMemo(() => ({
    mode: "payment",
    amount: amount && amount >= 0.5 ? Math.round(amount * 100) : 50,
    currency,
    paymentMethodCreation: "manual",
  }), [amount, currency]);

  // Defensive: don't render if no publishable key configured.
  if (!stripePromise) return null;
  // Stripe minimum is €0.50.
  if (!amount || amount < 0.5) return null;
  if (disabled) return null;

  return (
    <div data-testid="express-wallet-wrapper">
      <div className="text-center text-xs text-gray-500 mb-2">— Pagamento rapido —</div>
      <Elements stripe={stripePromise} options={options}>
        <ExpressInner getPayload={getPayload} total={amount} currency={currency} onError={onError} />
      </Elements>
      <div className="flex items-center gap-3 my-3">
        <div className="flex-1 h-px bg-gray-200" />
        <span className="text-xs text-gray-400">oppure</span>
        <div className="flex-1 h-px bg-gray-200" />
      </div>
    </div>
  );
};

export default ExpressWalletButtons;
